// Diffing for activity audit snapshots (`old_data` / `new_data` on activities).
//
// A snapshot is a raw DB row plus its child rows, so the diff must never lose
// track of WHICH child changed. Both sides are flattened into a map keyed by an
// identity path that embeds the child row's own id:
//
//     order_sale_products.754.kilo_price
//
// A price edit on a single sale line then yields exactly one entry, carrying the
// id. Diffing arrays positionally instead would report phantom changes whenever
// lines are reordered or an earlier line is removed — the failure this whole
// design exists to avoid.
//
// The flattener takes NO per-entity configuration: it works for every entity in
// `ActivityEntityName`, and a column added later shows up on its own. That is
// deliberate — a whitelist would fail SILENTLY (forget a field and it becomes
// invisible in the audit), which is the worst possible property for an audit log.
//
// See docs/features/ongoing/feature-activities-audit.md §3b.

// Columns that are pure write-bookkeeping and would otherwise fire on every
// single update.
//
// `active` is here because rows are soft-deleted (`active: -1`) rather than
// removed. NOTE this cuts both ways: the server MUST filter child collections to
// `active: 1` when building a snapshot, or a removed line arrives with only its
// `active` flag changed — which this list ignores — and the deletion becomes
// invisible. See §2c-1 of the feature doc.
const IGNORED_KEYS = ['created_at', 'updated_at', 'active', 'updated_by_id'];

export interface SnapshotChange {
    /** Identity path, e.g. `subtotal` or `order_sale_products.754.kilo_price`. */
    path: string;
    /** Value before the change; `undefined` when the key only exists after. */
    old: unknown;
    /** Value after the change; `undefined` when the key only existed before. */
    new: unknown;
}

export type Snapshot = Record<string, any> | null | undefined;

function hasKey(target: Record<string, unknown>, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(target, key);
}

// Treats `null` and `undefined` as the same absence, so a field that was null
// before and is missing after does not register as a change.
//
// Stringify comparison is sound here precisely BECAUSE flattening only ever
// emits scalars, and because the snapshot has already round-tripped through
// JSON: Prisma `DateTime` arrives as an ISO string, not a `Date`. That removes
// the whole date/decimal normalisation problem.
function valuesEqual(a: unknown, b: unknown): boolean {
    const left = a === undefined || a === null ? null : a;
    const right = b === undefined || b === null ? null : b;
    return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * Flattens a snapshot row into `{ identityPath: scalarValue }`.
 *
 * Child collections are detected at runtime (`Array.isArray` + an `id` on each
 * element), so nothing needs configuring — every table in this schema has an
 * autoincrement `id` primary key.
 */
export function flattenSnapshot(
    row: Snapshot,
    prefix: string = '',
): Record<string, unknown> {
    const flattened: Record<string, unknown> = {};
    if (!row) return flattened;

    const keys = Object.keys(row);

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (IGNORED_KEYS.indexOf(key) !== -1) continue;

        const value = (row as Record<string, unknown>)[key];

        if (Array.isArray(value)) {
            // A child collection. Key each row by its own id so identity
            // survives reordering and removal.
            for (let j = 0; j < value.length; j++) {
                const child = value[j];
                if (!child || typeof child !== 'object') continue;

                const childId = (child as { id?: unknown }).id;
                if (childId === undefined || childId === null) continue;

                const childFlat = flattenSnapshot(
                    child as Record<string, any>,
                    `${prefix}${key}.${childId}.`,
                );
                const childKeys = Object.keys(childFlat);
                for (let k = 0; k < childKeys.length; k++) {
                    flattened[childKeys[k]] = childFlat[childKeys[k]];
                }
            }
        } else if (value === null || typeof value !== 'object') {
            flattened[`${prefix}${key}`] = value;
        }
        // Nested single objects (e.g. the `products` row hanging off a sale
        // line) are skipped on purpose: they are carried for labelling in the
        // dialog, not for diffing. The line's own `product_id` is the auditable
        // fact; the product's columns belong to the product's own activities.
    }

    return flattened;
}

/**
 * Returns every field that differs between two snapshots, most useful shape
 * first: a flat list the dialog can render as a plain table.
 *
 * All four activity shapes fall out of the key union for free:
 *  - create  (`oldData` null)  -> every field reported as added
 *  - delete  (`newData` null)  -> every field reported as removed
 *  - added / removed child row -> only that row's paths
 *  - edited field             -> only that path
 *
 * A child row's own `id` is intentionally kept as a flattened field. It never
 * changes on an update, but it guarantees an added or removed line always
 * produces at least one entry even if all its other columns are null.
 */
export function diffSnapshots(
    oldData: Snapshot,
    newData: Snapshot,
): SnapshotChange[] {
    const oldFlat = flattenSnapshot(oldData);
    const newFlat = flattenSnapshot(newData);

    // Union of both key sets, preserving old-side order then appending
    // new-only keys. Built with plain arrays/objects rather than a Set: this
    // project targets es5 without downlevelIteration, so spreading a Set
    // iterator does not compile.
    const paths: string[] = Object.keys(oldFlat);
    const newPaths = Object.keys(newFlat);
    for (let i = 0; i < newPaths.length; i++) {
        if (!hasKey(oldFlat, newPaths[i])) {
            paths.push(newPaths[i]);
        }
    }

    const changes: SnapshotChange[] = [];
    for (let i = 0; i < paths.length; i++) {
        const path = paths[i];
        const before = oldFlat[path];
        const after = newFlat[path];

        if (!valuesEqual(before, after)) {
            changes.push({ path: path, old: before, new: after });
        }
    }

    return changes;
}
